import { useState, useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useUploadImage } from '@/hooks/useToolManagement/useUploadImage'
import type {
  ImportedImage,
  ToolRow,
  TrolleySheetData,
} from '@/utils/tool-management/tool-management.util'
import {
  buildImportedImageMap,
  countItemsByDrawer,
  extractDrawersFromSheet,
  extractTrolleyDescription,
  filterItemsByDrawer,
  getDataRowsWithoutDescription,
  transformTrolleySheetData,
} from '@/utils/tool-management/tool-management.util'
import { isValidRow } from '@/utils/tool-management/row-validation.util'
import {
  MAX_IMAGE_SIZE,
  validateImageSize,
} from '@/utils/tool-management/image-file.util'

const ITEMS_PER_PAGE = 10

export function usePreviewImportModal(
  isOpen: boolean,
  data: Nullable<TrolleySheetData>,
  images: ImportedImage[],
  onImagesUpdate?: (images: ImportedImage[]) => void
) {
  const { t } = useTranslation()
  const { uploadImages } = useUploadImage()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedTrolley, setSelectedTrolley] = useState<Nullable<string>>(null)
  const [selectedDrawer, setSelectedDrawer] = useState<Nullable<string>>(null)
  const [updatedImages, setUpdatedImages] = useState<ImportedImage[]>(images)
  const [currentImageLabel, setCurrentImageLabel] =
    useState<Nullable<string>>(null)

  useEffect(() => {
    setUpdatedImages(images)
  }, [images])

  const imageMap = useMemo(() => {
    return buildImportedImageMap(updatedImages)
  }, [updatedImages])

  const trolleys = useMemo(() => {
    return transformTrolleySheetData(data || {})
  }, [data])

  useEffect(() => {
    if (!isOpen) {
      setSelectedTrolley(null)
      setSelectedDrawer(null)
      return
    }

    if (trolleys.length > 0) {
      const trolleyExists = selectedTrolley
        ? trolleys.some(t => t.name === selectedTrolley)
        : false

      if (!trolleyExists || !selectedTrolley) {
        setSelectedTrolley(trolleys[0].name)
        setSelectedDrawer(null)
      }
    } else {
      setSelectedTrolley(null)
      setSelectedDrawer(null)
    }
  }, [isOpen, trolleys, selectedTrolley])

  const currentTrolleyData = useMemo(() => {
    if (!selectedTrolley || !data) return null
    const sheetData = data[selectedTrolley]
    return getDataRowsWithoutDescription(sheetData)
  }, [selectedTrolley, data])

  const currentTrolleyDescription = useMemo(() => {
    if (!selectedTrolley || !data) return null
    const sheetData = data[selectedTrolley]
    return extractTrolleyDescription(sheetData)
  }, [selectedTrolley, data])

  const drawers = useMemo(() => {
    const extractedDrawers = extractDrawersFromSheet(currentTrolleyData)
    return extractedDrawers.sort((a, b) => a.localeCompare(b))
  }, [currentTrolleyData])

  const drawerItemCounts = useMemo(() => {
    return countItemsByDrawer(currentTrolleyData)
  }, [currentTrolleyData])

  useEffect(() => {
    if (drawers.length > 0 && !selectedDrawer) {
      setSelectedDrawer(drawers[0])
    }
  }, [drawers, selectedDrawer])

  const trolleyNames = useMemo(() => {
    return trolleys.map(t => t.name)
  }, [trolleys])

  const trolleyCounts = useMemo(() => {
    return trolleys.map(t => extractDrawersFromSheet(t.data).length)
  }, [trolleys])

  const [currentPage, setCurrentPage] = useState(1)

  const allItems = useMemo(() => {
    return filterItemsByDrawer(currentTrolleyData, selectedDrawer)
  }, [currentTrolleyData, selectedDrawer])

  const totalPages = useMemo(() => {
    return Math.ceil(allItems.length / ITEMS_PER_PAGE)
  }, [allItems.length])

  const items = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    return allItems.slice(startIndex, endIndex)
  }, [allItems, currentPage])

  useEffect(() => {
    setCurrentPage(1)
  }, [selectedTrolley, selectedDrawer])

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage])

  const hasEmptyRows = useMemo(() => {
    return items.some((row: ToolRow) => !isValidRow(row))
  }, [items])

  const handleImageClick = (imageLabel: string) => {
    setCurrentImageLabel(imageLabel)
    fileInputRef.current?.click()
  }

  const handleImageRemove = (imageLabel: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const updated = updatedImages.filter(img => img.name !== imageLabel)
    setUpdatedImages(updated)

    if (onImagesUpdate) {
      onImagesUpdate(updated)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !currentImageLabel) return

    const isValidFormat =
      file.name.match(/\.(png|jpg|jpeg)$/i) ||
      file.type === 'image/png' ||
      file.type === 'image/jpeg' ||
      file.type === 'image/jpg'

    if (!isValidFormat) {
      alert(t('toolManagement.invalidImageFileType'))
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    if (!validateImageSize(file, MAX_IMAGE_SIZE)) {
      alert(t('toolManagement.imageSizeTooLarge'))
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    try {
      const uploadedImages = await uploadImages([file])
      const uploadedImage = uploadedImages[0]

      if (uploadedImage) {
        const newImage: ImportedImage = {
          name: currentImageLabel,
          presignedUrl: uploadedImage.presignedUrl,
          fileKey: uploadedImage.fileKey,
          tempKey: uploadedImage.tempKey,
          sessionId: uploadedImage.sessionId,
        }

        const updated = updatedImages.filter(
          img => img.name !== currentImageLabel
        )
        updated.push(newImage)
        setUpdatedImages(updated)

        if (onImagesUpdate) {
          onImagesUpdate(updated)
        }
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      alert('Failed to upload image. Please try again.')
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setCurrentImageLabel(null)
    }
  }

  return {
    fileInputRef,
    selectedTrolley,
    selectedDrawer,
    updatedImages,
    trolleys,
    trolleyNames,
    trolleyCounts,
    drawers,
    drawerItemCounts,
    items,
    hasEmptyRows,
    imageMap,
    currentPage,
    totalPages,
    currentTrolleyDescription,
    setCurrentPage,
    setSelectedTrolley,
    setSelectedDrawer,
    handleImageClick,
    handleImageRemove,
    handleImageUpload,
  }
}
